import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
    Search, Receipt, User, Calendar, DollarSign, 
    ChevronLeft, ChevronRight, RefreshCw, Filter,
    CheckCircle, XCircle, Clock, RotateCcw, Eye
} from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const statusConfig = {
    success: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle, label: 'Success' },
    failed: { color: 'bg-red-500/20 text-red-400', icon: XCircle, label: 'Failed' },
    pending: { color: 'bg-amber-500/20 text-amber-400', icon: Clock, label: 'Pending' },
    refunded: { color: 'bg-purple-500/20 text-purple-400', icon: RotateCcw, label: 'Refunded' }
};

const AdminTransactionsPage = () => {
    const { api, isSuperAdmin } = useAdmin();
    const [transactions, setTransactions] = useState([]);
    const [stats, setStats] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState({
        status: 'all',
        payment_method: 'all',
        search: ''
    });
    const [selectedTxn, setSelectedTxn] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [refundDialogOpen, setRefundDialogOpen] = useState(false);
    const [refundReason, setRefundReason] = useState('');
    const limit = 15;

    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ skip: page * limit, limit });
            if (filters.status !== 'all') params.append('status', filters.status);
            if (filters.payment_method !== 'all') params.append('payment_method', filters.payment_method);
            
            const response = await api.get(`/admin/transactions?${params}`);
            setTransactions(response.data.transactions);
            setTotal(response.data.total);
            setStats(response.data.stats);
        } catch (error) {
            toast.error('Failed to fetch transactions');
        } finally {
            setLoading(false);
        }
    }, [api, page, filters]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    const openDetailDialog = async (txn) => {
        try {
            const response = await api.get(`/admin/transactions/${txn.id}`);
            setSelectedTxn(response.data);
            setDetailDialogOpen(true);
        } catch (error) {
            toast.error('Failed to fetch transaction details');
        }
    };

    const handleRefund = async () => {
        if (!selectedTxn) return;
        try {
            await api.post(`/admin/transactions/${selectedTxn.id}/refund?reason=${encodeURIComponent(refundReason)}`);
            toast.success('Refund initiated successfully');
            setRefundDialogOpen(false);
            setDetailDialogOpen(false);
            setRefundReason('');
            fetchTransactions();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to process refund');
        }
    };

    const formatCurrency = (amount, currency) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD'
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-6" data-testid="admin-transactions-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Transactions</h1>
                    <p className="text-slate-400 mt-1">Monitor all payment transactions</p>
                </div>
                <Button 
                    variant="outline" 
                    onClick={fetchTransactions}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                </Button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="border-slate-700 bg-slate-800/50">
                        <CardContent className="p-4">
                            <p className="text-slate-400 text-sm">Total</p>
                            <p className="text-2xl font-bold text-white">{stats.total_transactions}</p>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-700 bg-slate-800/50">
                        <CardContent className="p-4">
                            <p className="text-green-400 text-sm flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Successful
                            </p>
                            <p className="text-2xl font-bold text-white">{stats.successful}</p>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-700 bg-slate-800/50">
                        <CardContent className="p-4">
                            <p className="text-red-400 text-sm flex items-center gap-1">
                                <XCircle className="w-3 h-3" /> Failed
                            </p>
                            <p className="text-2xl font-bold text-white">{stats.failed}</p>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-700 bg-slate-800/50">
                        <CardContent className="p-4">
                            <p className="text-amber-400 text-sm flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Pending
                            </p>
                            <p className="text-2xl font-bold text-white">{stats.pending}</p>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-700 bg-slate-800/50">
                        <CardContent className="p-4">
                            <p className="text-slate-400 text-sm">Revenue</p>
                            <div className="text-lg font-bold text-white">
                                {stats.revenue_by_currency && Object.entries(stats.revenue_by_currency).map(([currency, amount]) => (
                                    <div key={currency}>{formatCurrency(amount, currency)}</div>
                                ))}
                                {(!stats.revenue_by_currency || Object.keys(stats.revenue_by_currency).length === 0) && (
                                    <span>$0.00</span>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Filters */}
            <Card className="border-slate-700 bg-slate-800/50">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <Select 
                            value={filters.status} 
                            onValueChange={(v) => { setFilters(prev => ({ ...prev, status: v })); setPage(0); }}
                        >
                            <SelectTrigger className="w-[150px] bg-slate-700/50 border-slate-600 text-white">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="all" className="text-white">All Status</SelectItem>
                                <SelectItem value="success" className="text-white">Success</SelectItem>
                                <SelectItem value="failed" className="text-white">Failed</SelectItem>
                                <SelectItem value="pending" className="text-white">Pending</SelectItem>
                                <SelectItem value="refunded" className="text-white">Refunded</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select 
                            value={filters.payment_method} 
                            onValueChange={(v) => { setFilters(prev => ({ ...prev, payment_method: v })); setPage(0); }}
                        >
                            <SelectTrigger className="w-[150px] bg-slate-700/50 border-slate-600 text-white">
                                <SelectValue placeholder="Payment Method" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="all" className="text-white">All Methods</SelectItem>
                                <SelectItem value="moyasar" className="text-white">Moyasar</SelectItem>
                                <SelectItem value="paylink" className="text-white">Paylink</SelectItem>
                                <SelectItem value="stripe" className="text-white">Stripe</SelectItem>
                                <SelectItem value="paypal" className="text-white">PayPal</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Transactions Table */}
            <Card className="border-slate-700 bg-slate-800/50">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="transactions-table">
                            <thead>
                                <tr className="border-b border-slate-700">
                                    <th className="text-left p-4 text-slate-400 font-medium">Transaction</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">User</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Amount</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Method</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Date</th>
                                    <th className="text-right p-4 text-slate-400 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="p-8 text-center text-slate-400">
                                            Loading transactions...
                                        </td>
                                    </tr>
                                ) : transactions.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="p-8 text-center text-slate-400">
                                            <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                                            <p>No transactions found</p>
                                            <p className="text-sm text-slate-500 mt-1">Transactions will appear here once payments are processed</p>
                                        </td>
                                    </tr>
                                ) : (
                                    transactions.map((txn) => {
                                        const statusInfo = statusConfig[txn.status] || statusConfig.pending;
                                        const StatusIcon = statusInfo.icon;
                                        return (
                                            <tr key={txn.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                                                <td className="p-4">
                                                    <div>
                                                        <p className="text-white font-mono text-sm">{txn.id?.slice(0, 12)}...</p>
                                                        {txn.provider_txn_id && (
                                                            <p className="text-xs text-slate-500">Ref: {txn.provider_txn_id}</p>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    {txn.user ? (
                                                        <div className="flex items-center gap-2">
                                                            <User className="w-4 h-4 text-slate-500" />
                                                            <div>
                                                                <p className="text-slate-300 text-sm">{txn.user.name}</p>
                                                                <p className="text-xs text-slate-500">{txn.user.email}</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-slate-500">-</span>
                                                    )}
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-1">
                                                        <DollarSign className="w-4 h-4 text-slate-500" />
                                                        <span className="text-white font-medium">
                                                            {formatCurrency(txn.amount, txn.currency)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <Badge variant="outline" className="border-slate-600 text-slate-300 capitalize">
                                                        {txn.payment_method}
                                                    </Badge>
                                                </td>
                                                <td className="p-4">
                                                    <Badge className={`border-0 ${statusInfo.color}`}>
                                                        <StatusIcon className="w-3 h-3 mr-1" />
                                                        {statusInfo.label}
                                                    </Badge>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-2 text-sm text-slate-400">
                                                        <Calendar className="w-4 h-4" />
                                                        {formatDate(txn.created_at)}
                                                    </div>
                                                </td>
                                                <td className="p-4 text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => openDetailDialog(txn)}
                                                        className="text-slate-400 hover:text-white"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between p-4 border-t border-slate-700">
                            <p className="text-sm text-slate-400">
                                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(0, p - 1))}
                                    disabled={page === 0}
                                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={page >= totalPages - 1}
                                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Transaction Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Transaction Details</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            ID: {selectedTxn?.id}
                        </DialogDescription>
                    </DialogHeader>

                    {selectedTxn && (
                        <div className="space-y-4 py-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500">Amount</p>
                                    <p className="text-lg font-bold text-white">
                                        {formatCurrency(selectedTxn.amount, selectedTxn.currency)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Status</p>
                                    <Badge className={`${statusConfig[selectedTxn.status]?.color || 'bg-slate-500/20 text-slate-400'} border-0`}>
                                        {selectedTxn.status}
                                    </Badge>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Payment Method</p>
                                    <p className="text-white capitalize">{selectedTxn.payment_method}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Provider Transaction ID</p>
                                    <p className="text-white font-mono text-sm">{selectedTxn.provider_txn_id || '-'}</p>
                                </div>
                            </div>

                            {selectedTxn.user && (
                                <div className="border-t border-slate-700 pt-4">
                                    <p className="text-xs text-slate-500 mb-2">Customer</p>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                                            <User className="w-5 h-5 text-slate-400" />
                                        </div>
                                        <div>
                                            <p className="text-white">{selectedTxn.user.name}</p>
                                            <p className="text-sm text-slate-400">{selectedTxn.user.email}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="border-t border-slate-700 pt-4">
                                <p className="text-xs text-slate-500">Created</p>
                                <p className="text-white">{formatDate(selectedTxn.created_at)}</p>
                            </div>

                            {selectedTxn.error_message && (
                                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                    <p className="text-xs text-red-400 font-medium mb-1">Error</p>
                                    <p className="text-sm text-red-300">{selectedTxn.error_message}</p>
                                </div>
                            )}

                            {selectedTxn.refunded && (
                                <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                                    <p className="text-xs text-purple-400 font-medium mb-1">Refund Info</p>
                                    <p className="text-sm text-purple-300">Refunded on: {formatDate(selectedTxn.refunded_at)}</p>
                                    {selectedTxn.refund_reason && (
                                        <p className="text-sm text-purple-300">Reason: {selectedTxn.refund_reason}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setDetailDialogOpen(false)}
                            className="border-slate-600 text-slate-300"
                        >
                            Close
                        </Button>
                        {isSuperAdmin && selectedTxn?.status === 'success' && !selectedTxn?.refunded && (
                            <Button 
                                variant="destructive"
                                onClick={() => setRefundDialogOpen(true)}
                                className="bg-red-500 hover:bg-red-600"
                            >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Refund
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Refund Confirmation Dialog */}
            <AlertDialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
                <AlertDialogContent className="bg-slate-800 border-slate-700">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Confirm Refund</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            Are you sure you want to refund {selectedTxn && formatCurrency(selectedTxn.amount, selectedTxn.currency)}?
                            This action requires payment provider integration to complete.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-4">
                        <Input
                            placeholder="Reason for refund (optional)"
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                            className="bg-slate-700/50 border-slate-600 text-white"
                        />
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleRefund} className="bg-red-500 hover:bg-red-600">
                            Confirm Refund
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AdminTransactionsPage;
